import React, { useState, useEffect, useRef } from 'react';
import { supabase, isConfigured, storePendingMarketingConsent, checkRateLimits, logEvent } from '../services/supabase';
import { IMAGEMOTION_PACKAGES } from '@rehub/pricing';
import { Mail, Loader2, CheckCircle2, AlertCircle, Check } from 'lucide-react';
import SampleVideoDisclosure from './SampleVideoDisclosure';

const TESTER = IMAGEMOTION_PACKAGES.tester;

interface LoginProps {
  onAuthSuccess: () => void;
  onClose?: () => void;
  initialMode?: 'login' | 'signup';
  notice?: string | null;
}

const STAGING_SITE_URL = 'https://staginganimation.com';

function LoginLogos() {
  return (
    <div className="mb-4 flex flex-col items-center gap-2.5 px-8">
      <img
        src="/Logo.webp"
        alt="ImageMotion"
        width={624}
        height={85}
        className="h-6 w-auto max-w-full object-contain sm:h-7"
      />
      <span className="h-px w-8 bg-slate-600" aria-hidden />
      <a
        href={STAGING_SITE_URL}
        target="_blank"
        rel="noopener noreferrer"
        className="rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300"
      >
        <img
          src="/staging-animation-logo.png"
          alt="Staging Animation"
          width={817}
          height={122}
          className="h-7 w-auto max-w-[14rem] object-contain sm:h-8"
        />
      </a>
    </div>
  );
}

const fieldClass =
  'block w-full min-h-11 px-3 py-2.5 bg-slate-950 border border-slate-600 rounded-lg text-slate-100 placeholder-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:border-cyan-300';

const primaryButtonClass =
  'w-full inline-flex items-center justify-center gap-2 min-h-11 px-4 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0c1829] disabled:opacity-50 disabled:cursor-not-allowed';

function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isUnknownAccountError(err: { message?: string; code?: string; status?: number }): boolean {
  const message = `${err.message || ''} ${err.code || ''}`.toLowerCase();
  return (
    message.includes('signups not allowed') ||
    message.includes('user not found') ||
    message.includes('otp_disabled') ||
    err.code === 'otp_disabled'
  );
}

export default function Login({ initialMode = 'login', notice = null }: LoginProps) {
  const [mode, setMode] = useState<'login' | 'signup'>(initialMode);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [resent, setResent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unknownAccount, setUnknownAccount] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [acceptedMarketing, setAcceptedMarketing] = useState(false);
  const [website, setWebsite] = useState('');
  const submitLock = useRef(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) localStorage.setItem('referral_code', refCode);
      try {
        document.cookie = `rehub_referral_code=${encodeURIComponent(refCode)}; path=/; max-age=${60 * 60 * 24 * 14}; SameSite=Lax`;
      } catch {}
  }, []);

  useEffect(() => {
    if (!sent) return;
    document.getElementById('auth-dialog-title')?.focus();
  }, [sent]);

  const busy = loading || googleLoading;

  const validateEmail = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) return 'Enter your email address.';
    if (!isValidEmail(trimmed)) return 'Enter a valid email address.';
    return null;
  };

  const requireTerms = (): boolean => {
    if (mode !== 'signup') return true;
    if (acceptedTerms) {
      setTermsError(null);
      return true;
    }
    setTermsError('Accept the Terms and Conditions to continue.');
    document.getElementById('accept-terms')?.focus();
    return false;
  };

  const handleGoogleSignIn = async () => {
    if (busy || submitLock.current) return;
    setError(null);
    setUnknownAccount(false);
    if (!requireTerms()) return;
    if (!isConfigured) {
      setError('System Error: Supabase connection not configured.');
      return;
    }
    submitLock.current = true;
    setGoogleLoading(true);
    try {
      if (mode === 'signup' && acceptedMarketing) {
        localStorage.setItem('pending_marketing_consent', 'true');
      }
      const { error: oauthError } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: { access_type: 'offline', prompt: 'consent' },
        },
      });
      if (oauthError) throw oauthError;
    } catch (err: any) {
      console.error('Google Sign-In Error:', err);
      setError(err.message || 'Google sign-in didn’t start. Try again, or use email.');
      setGoogleLoading(false);
      submitLock.current = false;
    }
  };

  const sendMagicLink = async (isResend: boolean) => {
    if (submitLock.current) return;
    const trimmed = email.trim();
    const nextEmailError = validateEmail(trimmed);
    setEmailError(nextEmailError);
    setError(null);
    setUnknownAccount(false);
    setResent(false);
    if (nextEmailError) return;
    if (!requireTerms()) return;
    if (website.trim()) {
      setError('Something went wrong. Please try again.');
      return;
    }
    if (!isConfigured) {
      setError('System Error: Supabase connection not configured. Missing API Keys.');
      return;
    }

    submitLock.current = true;
    setLoading(true);
    try {
      const rateLimitCheck = await checkRateLimits(trimmed);
      if (!rateLimitCheck.allowed) {
        setError(rateLimitCheck.message || 'Too many attempts. Wait a few minutes, then try again.');
        return;
      }

      if (mode === 'signup' && acceptedMarketing) {
        await storePendingMarketingConsent(trimmed);
      }
      if (mode === 'signup') {
        localStorage.setItem('pending_marketing_consent', acceptedMarketing ? 'true' : 'false');
      }

      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: trimmed,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          shouldCreateUser: mode === 'signup',
          data:
            mode === 'signup'
              ? { signup_app: 'imagemotion', terms_accepted_at: new Date().toISOString() }
              : { signup_app: 'imagemotion' },
        },
      });
      if (otpError) throw otpError;

      await logEvent(
        'magic_link_requested',
        null,
        trimmed,
        null,
        navigator.userAgent,
        {
          mode,
          accepted_marketing: mode === 'signup' ? acceptedMarketing : false,
          accepted_terms: mode === 'signup',
          source_app: 'imagemotion',
          resend: isResend,
        }
      );

      setEmail(trimmed);
      setSent(true);
      if (isResend) setResent(true);
    } catch (err: any) {
      console.error('Auth Error:', err);
      if (mode === 'login' && isUnknownAccountError(err)) {
        setUnknownAccount(true);
        setError('We couldn’t find a ReHub account for that email.');
      } else if (err.message === 'Failed to fetch' || !navigator.onLine) {
        setError('Connection failed. Check your internet connection and try again.');
      } else if (err.status === 400) {
        setError('That email address wasn’t accepted. Check it and try again.');
      } else {
        setError(err.message || 'The sign-in link wasn’t sent. Try again.');
      }
    } finally {
      setLoading(false);
      submitLock.current = false;
    }
  };

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    await sendMagicLink(false);
  };

  const toggleMode = () => {
    setMode(mode === 'login' ? 'signup' : 'login');
    setError(null);
    setEmailError(null);
    setTermsError(null);
    setUnknownAccount(false);
    setSent(false);
    setResent(false);
    setAcceptedTerms(false);
    setAcceptedMarketing(false);
    setWebsite('');
  };

  if (sent) {
    return (
      <div className="relative px-6 pt-4 pb-6 sm:px-8">
        <div className="text-center">
          <LoginLogos />
          <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-emerald-900/40 text-emerald-300">
            <CheckCircle2 size={22} aria-hidden />
          </div>
          <h2 id="auth-dialog-title" tabIndex={-1} className="px-8 text-lg font-semibold leading-snug text-white outline-none sm:text-xl">
            Check your email
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-slate-200" role="status">
            {resent ? 'We’ve sent another secure link to ' : 'We’ve sent a secure sign-in link to '}
            <span className="font-medium text-white">{email}</span>.
            Open it on this device to {mode === 'signup' ? 'create your account' : 'continue'}.
          </p>
          {error && (
            <div role="alert" className="mt-4 flex items-start gap-2 p-3 bg-red-950/50 border border-red-800/80 rounded-lg text-red-200 text-sm text-left">
              <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden />
              <span>{error}</span>
            </div>
          )}
          <div className="mt-6 flex flex-col gap-2">
            <button
              type="button"
              onClick={() => sendMagicLink(true)}
              disabled={loading}
              className={primaryButtonClass}
            >
              {loading ? (
                <>
                  <Loader2 size={18} className="animate-spin" aria-hidden />
                  <span>Sending…</span>
                </>
              ) : (
                'Resend link'
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setSent(false);
                setResent(false);
                setError(null);
              }}
              className="min-h-11 text-sm font-medium text-cyan-200 hover:text-white underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 rounded"
            >
              Try a different email
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isSignup = mode === 'signup';

  return (
    <div className="relative px-6 pt-4 pb-6 sm:px-8">
      {notice && (
        <div role="alert" className="mb-4 p-3 rounded-lg bg-red-950/60 border border-red-800/80 text-red-200 text-sm">
          {notice}
        </div>
      )}
      <div className="mb-4 text-center">
        <LoginLogos />
        <h2 id="auth-dialog-title" className="px-8 text-lg font-semibold leading-snug text-white sm:text-xl">
          {isSignup ? 'Create your free sample video' : 'Welcome back'}
        </h2>
        <p id="auth-dialog-description" className="mt-2 text-sm leading-relaxed text-slate-200">
          {isSignup
            ? `Up to ${TESTER.maxImages} photos · Small watermark · No card required.`
            : 'Sign in with your ReHub account to continue.'}
        </p>
        {isSignup ? (
          <SampleVideoDisclosure idPrefix="signup" />
        ) : (
          <p className="mt-2 text-sm text-slate-300">One account for all ReHub apps.</p>
        )}
      </div>

      {error && (
        <div role="alert" className="flex items-start gap-2 p-3 bg-red-950/50 border border-red-800/80 rounded-lg text-red-200 text-sm mb-4">
          <AlertCircle size={16} className="shrink-0 mt-0.5" aria-hidden />
          <div>
            <p>{error}</p>
            {unknownAccount && (
              <button
                type="button"
                onClick={toggleMode}
                className="mt-2 font-medium text-cyan-200 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 rounded"
              >
                Create your free sample video
              </button>
            )}
          </div>
        </div>
      )}

      <form onSubmit={handleAuth} noValidate className="space-y-4" aria-busy={busy}>
        <div className="absolute -left-[9999px] top-0 h-0 w-0 overflow-hidden" aria-hidden="true">
          <label htmlFor="website">Website</label>
          <input
            type="text"
            id="website"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
          />
        </div>

        <div className="space-y-1.5 text-left">
          <label htmlFor="email" className="block text-sm font-medium text-slate-100">
            Email address
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
              <Mail size={18} aria-hidden />
            </div>
            <input
              id="email"
              name="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              spellCheck={false}
              value={email}
              disabled={busy}
              onChange={(e) => {
                setEmail(e.target.value);
                if (emailError) setEmailError(validateEmail(e.target.value));
              }}
              onBlur={() => {
                if (email.trim()) setEmailError(validateEmail(email));
              }}
              onFocus={(event) => {
                event.currentTarget.scrollIntoView({ block: 'center' });
              }}
              placeholder="you@example.com"
              aria-invalid={emailError ? true : undefined}
              aria-describedby={emailError ? 'email-error email-hint' : 'email-hint'}
              className={`${fieldClass} pl-10 ${emailError ? 'border-red-400' : ''}`}
            />
          </div>
          <p id="email-hint" className="text-sm leading-relaxed text-slate-300">
            {isSignup
              ? 'A secure link will arrive by email. Open it to create your account.'
              : 'A secure link will arrive by email.'}
          </p>
          {emailError && (
            <p id="email-error" role="alert" className="text-sm text-red-300">
              {emailError}
            </p>
          )}
        </div>

        {isSignup && (
          <div className="space-y-3 text-left">
            <div>
              <label className="flex items-start gap-3 cursor-pointer min-h-11">
                <span className="relative mt-0.5 flex">
                  <input
                    id="accept-terms"
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => {
                      setAcceptedTerms(e.target.checked);
                      if (e.target.checked) setTermsError(null);
                    }}
                    aria-invalid={termsError ? true : undefined}
                    aria-describedby={termsError ? 'terms-error' : undefined}
                    className="peer h-5 w-5 shrink-0 rounded border-slate-500 bg-slate-950 appearance-none border checked:bg-blue-600 checked:border-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0c1829]"
                  />
                  <Check className="absolute top-1 left-0.5 h-3 w-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none" strokeWidth={3.5} aria-hidden />
                </span>
                <span className="text-sm text-slate-200 leading-relaxed">
                  I accept the{' '}
                  <a href="https://rehub.studio/terms" target="_blank" rel="noopener noreferrer" className="text-cyan-200 underline underline-offset-2 hover:text-white" onClick={(e) => e.stopPropagation()}>
                    Terms and Conditions
                  </a>{' '}
                  and{' '}
                  <a href="https://rehub.studio/privacy" target="_blank" rel="noopener noreferrer" className="text-cyan-200 underline underline-offset-2 hover:text-white" onClick={(e) => e.stopPropagation()}>
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>
              {termsError && (
                <p id="terms-error" role="alert" className="mt-1 text-sm text-red-300">
                  {termsError}
                </p>
              )}
            </div>

            <label className="flex items-start gap-3 cursor-pointer min-h-11">
              <span className="relative mt-0.5 flex">
                <input
                  type="checkbox"
                  checked={acceptedMarketing}
                  onChange={(e) => setAcceptedMarketing(e.target.checked)}
                  className="peer h-5 w-5 shrink-0 rounded border-slate-500 bg-slate-950 appearance-none border checked:bg-blue-600 checked:border-blue-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0c1829]"
                />
                <Check className="absolute top-1 left-0.5 h-3 w-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none" strokeWidth={3.5} aria-hidden />
              </span>
              <span className="text-sm text-slate-200 leading-relaxed">
                I agree to receive marketing, offers, and updates from ReHub Studio.{' '}
                <span className="text-slate-400">(Optional)</span>
              </span>
            </label>
          </div>
        )}

        <button type="submit" disabled={busy} className={primaryButtonClass}>
          {loading ? (
            <>
              <Loader2 size={18} className="animate-spin" aria-hidden />
              <span>Sending…</span>
            </>
          ) : (
            'Email me a sign-in link'
          )}
        </button>
      </form>

      <div className="relative my-5">
        <div className="absolute inset-0 flex items-center" aria-hidden="true">
          <div className="w-full border-t border-slate-700" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-3 bg-[#0c1829] text-slate-300">or continue with</span>
        </div>
      </div>

      <button
        type="button"
        onClick={handleGoogleSignIn}
        disabled={busy}
        className="w-full inline-flex items-center justify-center gap-3 min-h-11 px-4 rounded-lg border border-slate-300 bg-white text-slate-900 font-semibold shadow-sm transition-colors hover:border-cyan-200 hover:bg-slate-50 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0c1829] disabled:opacity-50 disabled:cursor-not-allowed motion-reduce:transition-none"
      >
        {googleLoading ? (
          <>
            <Loader2 size={18} className="animate-spin text-slate-600" aria-hidden />
            <span>Continuing…</span>
          </>
        ) : (
          <>
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Continue with Google
          </>
        )}
      </button>

      <div className="mt-5 pt-4 border-t border-slate-800 text-center">
        {isSignup ? (
          <p className="text-sm text-slate-200">
            Already have an account?{' '}
            <button type="button" onClick={toggleMode} className="font-medium text-cyan-200 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 rounded">
              Sign in
            </button>
          </p>
        ) : (
          <p className="text-sm text-slate-200">
            New to ImageMotion?{' '}
            <button type="button" onClick={toggleMode} className="font-medium text-cyan-200 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 rounded">
              Create your free sample video
            </button>
          </p>
        )}
        {!isSignup && (
          <p className="mt-3 text-sm text-slate-200">
            <a href="https://rehub.studio/terms" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 rounded">
              Terms
            </a>
            <span aria-hidden="true"> · </span>
            <a href="https://rehub.studio/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 rounded">
              Privacy
            </a>
          </p>
        )}
      </div>
    </div>
  );
}
